import type PlyrType from "plyr";
import "plyr/dist/plyr.css";
import { useEffect, useRef } from "react";

export default function VideoPlayer({
  videoFilename,
}: {
  videoFilename: string;
}) {
  const playerRef = useRef<PlyrType | null>(null);
  const videoElementRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    if (!videoElementRef.current) return;

    let destroyed = false;

    const initializePlyr = async () => {
      try {
        const { default: Plyr } = await import("plyr");

        if (destroyed || !videoElementRef.current) return;

        // Ensure we don't try to initialize on a non-existent or already Plyr-enhanced element
        // Basic check; a more robust one might involve checking for Plyr specific classes/attributes
        if (videoElementRef.current.classList.contains("plyr--instance-id")) {
          console.warn("Plyr already initialized on this element");
          return;
        }

        playerRef.current = new Plyr(videoElementRef.current, {
          controls: [
            "play-large",
            "play",
            "progress",
            "current-time",
            "mute",
            "volume",
            "captions",
            "settings",
            "pip",
            "airplay",
            "fullscreen",
          ],
          settings: ["captions", "quality", "speed", "loop"],
          speed: { selected: 1, options: [0.5, 0.75, 1, 1.25, 1.5, 2] },
        });
      } catch (error) {
        console.error("Failed to load or initialize Plyr:", error);
      }
    };

    void initializePlyr();

    return () => {
      destroyed = true;
      if (playerRef.current) {
        playerRef.current.destroy();
        playerRef.current = null;
      }
    };
  }, []); // Only run once on mount, video source is handled by the <source> tag

  return (
    <div className="video-container aspect-video w-full max-w-4xl mx-auto my-4 bg-black rounded-lg shadow-xl overflow-hidden">
      <video
        ref={videoElementRef}
        id={`player-${videoFilename}`}
        controls
        playsInline
      >
        {/* The source is set dynamically by Plyr via the useEffect hook,
            but providing a source tag can be good for initial load or non-JS scenarios.
            Alternatively, Plyr can initialize directly from this if `player.source` is not set in useEffect.
        */}
        <source src={`/api/videos/${videoFilename}`} type="video/mp4" />
        Your browser does not support the video tag.
      </video>
    </div>
  );
}
